/**
 * ModulesContext — `enabled`-флаги виджетов витрины (FE-11, US-09 FR-1):
 * по ним прячется всё, что показывает данные, — левое меню, блоки
 * «Цифрового паспорта», плашки и виджеты «Аналитики» и «Проектов»
 * (список идентификаторов — `src/consts/modules.ts`).
 *
 * - Любой пользователь, включая супер-админа и **анонима**: read-only
 *   `/api/public/modules?kato=` (US-09 FR-1.4) — видимость считается
 *   бэкендом для **уровня выбранной территории** (DP-365), поэтому при
 *   смене КАТО флаги перезапрашиваются. Свёрнутый `/super-admin/modules`
 *   витрине не подходит: там `enabled` — конъюнкция по уровням.
 * - Экран настроек (`/admin/modules`) ходит за полной матрицей сам.
 * - До того как известно, кто перед нами (Keycloak не инициализирован,
 *   userinfo ещё не приехал): пустой `Map`, `useModulePresent` возвращает
 *   `true` (default-visible).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { KatoContext } from './KatoContext';
import { useUser } from './UserContext';
import { useKeycloak } from '../keycloak/KeycloakProvider';
import { listPublicModules } from '../requests/public.request';
import {
  setModuleEnabled as setModuleEnabledRequest,
  type ModuleInfo,
} from '../requests/super_admin.request';

interface ModulesContextValue {
  /** Map<moduleId, ModuleInfo>. Пустой до загрузки и при её ошибке. */
  modules: ReadonlyMap<string, ModuleInfo>;
  loading: boolean;
  /**
   * Последний фетч упал (сеть/403/5xx). Пустой `modules` при `error` —
   * «не смогли загрузить», а не «модулей нет»: admin-UI показывает ошибку,
   * а не «Нет данных»; для витрины разницы нет — default-visible.
   */
  error: boolean;
  /** Toggle для super-admin (PUT BE-11). Возвращает обновлённый объект. */
  setEnabled: (moduleId: string, enabled: boolean) => Promise<void>;
  /**
   * Перечитать флаги витрины с бэка для текущей учётки и территории.
   * Нужен экрану настроек «Видимость витрины»: после сохранения тумблеров
   * витрина обязана обновиться сразу, без перезагрузки страницы
   * (US-09 FR-1.3) — сохранение идёт мимо `setEnabled`, ячейками матрицы.
   */
  refresh: () => Promise<void>;
}

const EMPTY_MAP: ReadonlyMap<string, ModuleInfo> = new Map();

/** Значение `viewer` для неавторизованного: флаги ему тоже положены. */
const ANONYMOUS = '__anonymous__';

/** Внутренний context — экспортируется для useModulePresent. */
export const ModulesContext = createContext<ModulesContextValue | null>(null);

export const ModulesProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const { initialized, keycloak } = useKeycloak();
  // Мягко: без KatoProvider (unit-тесты компонентов) флаги грузятся без
  // привязки к территории, а не падают.
  const selectedKato = useContext(KatoContext)?.selectedKato ?? null;
  const [modules, setModules] = useState<ReadonlyMap<string, ModuleInfo>>(EMPTY_MAP);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Номер последнего запроса: ответ устаревшего не перетирает свежие флаги.
  const requestSeq = useRef(0);

  const userId = user?.user_id ?? null;
  const authenticated = keycloak.authenticated === true;
  /**
   * Чью витрину считаем. `null` — пока неизвестно (Keycloak ещё не
   * инициализирован / userinfo залогиненного не приехал), флаги не спрашиваем.
   *
   * - залогиненный: ждём `/user/userinfo` — запрос уйдёт с Bearer, и бэкенд
   *   посчитает видимость под его роль; смена учётки меняет ключ и
   *   перезапрашивает флаги;
   * - аноним: спрашиваем сразу после init — токена не будет вовсе, бэкенд
   *   отдаёт карту и без него. Раньше условием было `userId !== null`, и
   *   аноним не запрашивал флаги никогда: на стенде с открытым
   *   `/api/public/*` (`PUBLIC_READ_REQUIRES_AUTH=false`) он видел
   *   выключенные супер-админом виджеты (DP-365, US-09 FR-1.4).
   */
  const viewer = authenticated ? userId : initialized ? ANONYMOUS : null;

  /**
   * Загрузка флагов витрины. Витрина у всех ролей считается одинаково — по
   * `/public/modules` для выбранной территории: супер-админ задаёт видимость
   * отдельно на каждый уровень КАТО (DP-365), а свёрнутая форма
   * `/super-admin/modules` отдаёт конъюнкцию по уровням и на витрине скрывала
   * бы виджет, выключенный на одном уровне, сразу на всех. Label'ы витрине не
   * нужны — только enabled-флаги для `useModulePresent`.
   *
   * Зависимости: `viewer` — стала известна учётка (или анонимность), либо
   * сменился пользователь; `selectedKato` — смена территории: у другого
   * уровня КАТО другой набор включённых виджетов.
   */
  const load = useCallback(async (): Promise<void> => {
    if (viewer === null) {
      // Ещё не знаем, кто перед нами (или пользователь вышел): гонки нет,
      // но номер двигаем — ответ прежней учётки не должен долететь и
      // вернуть её флаги.
      requestSeq.current += 1;
      setModules(EMPTY_MAP);
      setLoading(false);
      setError(false);
      return;
    }
    const seq = (requestSeq.current += 1);
    setLoading(true);
    setError(false);
    try {
      const list = await listPublicModules(selectedKato);
      // Пока запрос шёл, начался следующий (смена КАТО, повторный refresh) —
      // его ответ и есть актуальный, этот молча выбрасываем.
      if (seq !== requestSeq.current) return;
      setModules(
        new Map<string, ModuleInfo>(
          list.map((m) => [m.module_id, { ...m, label_ru: null, label_kz: null }]),
        ),
      );
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setModules(EMPTY_MAP);
      setError(true);
      console.error('modules fetch failed', err);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [viewer, selectedKato]);

  useEffect(() => {
    void load();
  }, [load]);

  const setEnabled = useCallback(
    async (moduleId: string, enabled: boolean) => {
      const updated = await setModuleEnabledRequest(moduleId, enabled);
      setModules((current) => {
        const next = new Map(current);
        next.set(moduleId, updated);
        return next;
      });
    },
    [],
  );

  return (
    <ModulesContext.Provider
      value={{ modules, loading, error, setEnabled, refresh: load }}
    >
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = (): ModulesContextValue => {
  const ctx = useContext(ModulesContext);
  if (!ctx) {
    throw new Error('useModules must be used inside <ModulesProvider>');
  }
  return ctx;
};
